import type {
  GamePlayerProjectionsRequest,
  Status
} from '@library/types/projection';
import { useCallback, useState } from 'react';
import type { ConfirmationModalProps } from '@library/components/modals/ConfirmationModal';
import { DateTime } from 'luxon';
import { SelectPopoverContainer } from '@library/components/selects/SelectPopoverContainer';
import { StatusSelect } from '@library/components/selects';
import { formatWithTimezoneChange } from '@library/utils/dateTime';
import { getTooltipForStatus } from '@library/utils/status';
import type { useUpdateBulkProjectionsMutation } from '@bo-services/projectionsApi';

export const BulkBoardStatusButton = ({
  bulkUpdateProjections,
  setConfirmationModalProps,
  tradingQueryParams
}: {
  bulkUpdateProjections: ReturnType<typeof useUpdateBulkProjectionsMutation>[0];
  setConfirmationModalProps: (
    props: Omit<ConfirmationModalProps, 'onCancelModal'> | false
  ) => void;
  tradingQueryParams: GamePlayerProjectionsRequest;
}) => {
  const {
    durationId,
    fromDate,
    gameId,
    leagueId,
    search,
    statTypeIds,
    status,
    team,
    toDate
  } = tradingQueryParams;

  const [bulkStatus, setBulkStatus] = useState<Status>('pre_game');

  const handleConfirmBulkUpdateStatus = useCallback(async () => {
    await bulkUpdateProjections({
      league_ids: leagueId ?? [],
      from_date: formatWithTimezoneChange(
        DateTime.fromISO(fromDate),
        'LL/dd/yyyy'
      ),
      to_date:
        toDate &&
        formatWithTimezoneChange(DateTime.fromISO(toDate), 'LL/dd/yyyy'),
      search: search ?? '',
      game_ids: gameId ? [gameId] : undefined,
      teams: team ? [team] : undefined,
      stat_type_ids: statTypeIds ?? undefined,
      statuses: status,
      duration_ids: durationId,
      ignore_update_limit: false,
      projection: {
        status: bulkStatus
      }
    });
  }, [
    bulkUpdateProjections,
    leagueId,
    fromDate,
    toDate,
    search,
    gameId,
    team,
    statTypeIds,
    status,
    durationId,
    bulkStatus
  ]);

  const handleOnSaveBulkStatus = () => {
    setConfirmationModalProps({
      confirmationCopy: 'Change',
      handleConfirm: handleConfirmBulkUpdateStatus,
      label: 'Bulk Changing Status',
      children: `Are you sure you want to bulk change the status for all projections that match your search criteria to ${getTooltipForStatus(
        bulkStatus
      )}?`,
      isOpen: true
    });
  };

  return (
    <SelectPopoverContainer
      buttonText="Bulk Status"
      handleOnClick={handleOnSaveBulkStatus}
    >
      <StatusSelect
        containerClassName="flex"
        className="items-center justify-self-center !pl-0 !pr-8"
        label="Bulk Status"
        onChange={(newStatus) => setBulkStatus(newStatus)}
        value={bulkStatus}
      />
    </SelectPopoverContainer>
  );
};
